/**
 * Identity module DTOs (§3, §15.1, §17.3, D-003): staff authentication (magic link + TOTP, dev login code),
 * tenant sessions, participant profiles (masked by default) and permissioned unmasking with purpose.
 */
import { z } from 'zod';
import { E164Phone, IsoDateTime, LocaleTag, SocialHandle, Ulid } from './common.js';
import { SocialPlatform, StaffRole } from './enums.js';
import { Permission } from './rbac.js';
export const StaffLoginRequest = z.object({ email: z.email(), tenantSlug: z.string().max(64).optional() });
export const StaffLoginResponse = z.object({
    challengeId: Ulid,
    method: z.enum(['MAGIC_LINK', 'DEV_CODE']),
    expiresAt: IsoDateTime,
});
export const VerifyLoginRequest = z.object({
    challengeId: Ulid,
    /** Magic-link token or the dev login code (D-003). Never logged. */
    code: z.string().min(6).max(128),
    totp: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
});
export const StepUpRequest = z.object({
    totp: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
    passkeyAssertion: z.record(z.string(), z.unknown()).optional(),
});
export const StaffSessionDto = z.object({
    staffId: Ulid,
    tenantId: Ulid.nullable(),
    tenantSlug: z.string().nullable(),
    email: z.string(),
    name: z.string(),
    roles: z.array(StaffRole),
    permissions: z.array(Permission),
    /** Sponsor viewers are scoped to one sponsor (D11). */
    sponsorId: Ulid.nullable(),
    mfaEnrolled: z.boolean(),
    stepUpUntil: IsoDateTime.nullable(),
    /** Super-admin just-in-time grant (§3). */
    jitGrant: z
        .object({ reason: z.string(), grantedUntil: IsoDateTime, ticketRef: z.string().optional() })
        .nullable(),
    expiresAt: IsoDateTime,
});
export const JitGrantRequest = z.object({
    tenantId: Ulid,
    reason: z.string().min(10).max(500),
    ticketRef: z.string().max(80).optional(),
    minutes: z.number().int().min(5).max(240).default(60),
});
export const StaffUserDto = z.object({
    staffId: Ulid,
    email: z.string(),
    name: z.string(),
    roles: z.array(StaffRole),
    sponsorId: Ulid.nullable(),
    active: z.boolean(),
    lastLoginAt: IsoDateTime.nullable(),
});
export const InviteStaffRequest = z.object({
    email: z.email(),
    name: z.string().min(1).max(120),
    roles: z.array(StaffRole).min(1),
    sponsorId: Ulid.optional(),
});
// --- participants (no accounts; one profile per participant across platforms, §12.5) -------------------
export const ParticipantProfileDto = z.object({
    participantId: Ulid,
    displayName: z.string(),
    channels: z.array(z.object({
        channelId: Ulid,
        platform: SocialPlatform,
        valueMasked: z.string(),
        primary: z.boolean(),
        linkedAt: IsoDateTime,
    })),
    sourcePlatform: SocialPlatform.nullable(),
    locale: LocaleTag.nullable(),
    lotteriesEntered: z.number().int(),
    prizesWon: z.number().int(),
    consents: z.object({
        sponsorCommunication: z.boolean(),
        marketing: z.boolean(),
        winnerPublicity: z.boolean(),
        marketingTextVersion: z.string().nullable(),
        marketingRecordedAt: IsoDateTime.nullable(),
        marketingWithdrawnAt: IsoDateTime.nullable(),
    }),
    riskFlags: z.array(z.object({ code: z.string(), at: IsoDateTime, reviewed: z.boolean() })),
    createdAt: IsoDateTime,
});
/** §16.3: unmasking requires permission, purpose and audit. */
export const UnmaskRequest = z.object({
    subject: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('PARTICIPANT_CONTACT'), participantId: Ulid, channelId: Ulid }),
        z.object({ kind: z.literal('CLAIM_IDENTITY'), claimId: Ulid }),
        z.object({ kind: z.literal('PAYOUT_DESTINATION'), payoutId: Ulid }),
    ]),
    purpose: z.string().min(10).max(300),
});
export const UnmaskResponse = z.object({
    value: z.string(),
    /** Short-lived: the UI re-masks after this instant. */
    validUntil: IsoDateTime,
    auditId: Ulid,
});
export const ConsentRecordDto = z.object({
    consentId: Ulid,
    participantId: Ulid,
    purpose: z.enum(['PARTICIPATION', 'SPONSOR_COMMUNICATION', 'MARKETING', 'WINNER_PUBLICITY']),
    granted: z.boolean(),
    textVersion: z.string(),
    platform: SocialPlatform.nullable(),
    source: z.string(),
    recordedAt: IsoDateTime,
});
export const WithdrawConsentRequest = z.object({
    purpose: z.enum(['SPONSOR_COMMUNICATION', 'MARKETING', 'WINNER_PUBLICITY']),
    channel: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('WHATSAPP'), value: E164Phone }),
        z.object({ kind: z.literal('SOCIAL_HANDLE'), platform: SocialPlatform, value: SocialHandle }),
    ]),
});
//# sourceMappingURL=identity.js.map